import { useTranslation } from 'react-i18next';
import {
  Maximize, Pause, Play, Volume2, VolumeX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { formatFrameClock } from '@/lib/ai-assistant/editor/frameCapture';

interface Props {
  isPlaying: boolean;
  isMuted: boolean;
  /** Seconds on the clip timeline. */
  currentTime: number;
  duration: number;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onSeek: (clipTime: number) => void;
  onFullscreen: () => void;
}

// AI Studio's player controls: play/pause, a scrub bar on the clip timeline
// with the time, mute and fullscreen. Everything else is done by talking to
// the assistant.
export function AiStudioTransport({
  isPlaying, isMuted, currentTime, duration, onTogglePlay, onToggleMute, onSeek, onFullscreen,
}: Props) {
  const { t } = useTranslation();
  const end = Math.max(0, duration);
  const at = Math.min(Math.max(0, currentTime), end);
  return (
    <div className="flex items-center gap-2" data-testid="ai-studio-transport">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onTogglePlay}
        aria-label={t(isPlaying ? 'aiAssistant.studio.pause' : 'aiAssistant.studio.play')}
        data-testid="ai-studio-play"
      >
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </Button>
      <Slider
        value={[at]}
        min={0}
        max={end || 1}
        step={0.1}
        onValueChange={([value]) => onSeek(value)}
        className="flex-1"
        aria-label={t('aiAssistant.studio.seek')}
        data-testid="ai-studio-scrub"
      />
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground" data-testid="ai-studio-time">
        {formatFrameClock(at)} / {formatFrameClock(end)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onToggleMute}
        aria-label={t(isMuted ? 'aiAssistant.studio.unmute' : 'aiAssistant.studio.mute')}
        data-testid="ai-studio-mute"
      >
        {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onFullscreen}
        aria-label={t('aiAssistant.studio.fullscreen')}
        data-testid="ai-studio-fullscreen"
      >
        <Maximize className="h-4 w-4" />
      </Button>
    </div>
  );
}
